import {
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
  merge,
  of,
  scan,
  shareReplay,
  switchMap,
  timer,
} from 'rxjs';
import { serviceMonitor, SignalStrength } from './ServiceMonitor';

// The user-facing collapse of the monitor's layers: one phase at a time,
// priority ordered. Offline masks lureoDown.
export type ConnectionPhase = 'creating' | 'offline' | 'lureoDown' | 'recovered';

export type ConnectionUx = {
  phase: ConnectionPhase;
  /** The phase has outlasted a blip. lureoDown and recovered show the overlay card; offline shows none, and editing carries on. */
  visible: boolean;
};

const OVERLAY_DELAY_MS = 2000;

const recoveredDismiss = new Subject<void>();

/** The Continue button on the "We're back" prompt. */
export function dismissRecovered() {
  recoveredDismiss.next();
}

type RawPhase = 'creating' | 'offline' | 'lureoDown';

export type ConnectionUxInputs = {
  network$: Observable<SignalStrength>;
  disruption$: Observable<boolean>;
  dismissRecovered$: Observable<void>;
};

type PhaseEvent = { type: 'raw'; phase: RawPhase } | { type: 'dismiss' };

export function connectionUxFrom(inputs: ConnectionUxInputs): Observable<ConnectionUx> {
  const rawPhase$: Observable<RawPhase> = combineLatest([inputs.network$, inputs.disruption$]).pipe(
    map(([network, disruption]): RawPhase => {
      if (network === SignalStrength.None) return 'offline';
      if (disruption) return 'lureoDown';
      return 'creating';
    }),
    distinctUntilChanged()
  );

  // A lureoDown that clears does not silently vanish: it becomes the 'recovered'
  // prompt and waits for the user to continue.
  const phase$: Observable<ConnectionPhase> = merge(
    rawPhase$.pipe(map((phase): PhaseEvent => ({ type: 'raw', phase }))),
    inputs.dismissRecovered$.pipe(map((): PhaseEvent => ({ type: 'dismiss' })))
  ).pipe(
    scan((current: ConnectionPhase, event: PhaseEvent): ConnectionPhase => {
      if (event.type === 'dismiss') return current === 'recovered' ? 'creating' : current;
      if (event.phase === 'creating' && current === 'lureoDown') return 'recovered';
      if (event.phase === 'creating' && current === 'recovered') return 'recovered';
      return event.phase;
    }, 'creating' as ConnectionPhase),
    distinctUntilChanged()
  );

  return phase$.pipe(
    switchMap((phase): Observable<ConnectionUx> => {
      if (phase === 'offline') {
        // Visible once it outlasts a short blip window, so a wifi hop is not counted as going offline
        return merge(
          of({ phase, visible: false }),
          timer(OVERLAY_DELAY_MS).pipe(map(() => ({ phase, visible: true })))
        );
      }
      return of({ phase, visible: phase !== 'creating' });
    })
  );
}

export const connectionUx$: Observable<ConnectionUx> = connectionUxFrom({
  network$: serviceMonitor.networkStrength$,
  disruption$: serviceMonitor.serviceDisruption$,
  dismissRecovered$: recoveredDismiss,
}).pipe(shareReplay({ bufferSize: 1, refCount: true }));

/** Synchronous read of the disruption feed, for the entry gate's first render. */
export function isDisruptionActive(): boolean {
  let active = false;
  serviceMonitor.serviceDisruption$.subscribe(value => (active = value)).unsubscribe();
  return active;
}
